'use client';

import React from 'react';
import { clampExcerpt } from '@/helpers/excerpt';
import {
  StyledContentWrapper,
  StyledSectionLabel,
  StyledSectionHeading,
  StyledSectionIntro,
  StyledSection,
  StyledInner,
  StyledStepsGrid,
  StyledStep,
  StyledFormulaBox,
  StyledFormulaBody,
  StyledDataTable,
  StyledBadge,
  StyledTableFootnote,
  StyledFactorsGrid,
  StyledFactorCard,
  StyledTipsGrid,
  StyledTipCard,
  StyledMetricsTable,
  StyledRelatedGrid,
  StyledRelatedCard,
} from './WordCounterContent.styles';

interface IRelatedPost {
  _id?: string;
  name: string;
  slug: string;
  excerpt: string;
}

interface IProps {
  relatedPosts: IRelatedPost[];
}

const steps = [
  {
    number: '1',
    title: 'Paste your text',
    description:
      'Copy and paste any text: blog posts, articles, product descriptions, scripts, social media captions, or any other content. The tool accepts plain text of any length. Free, no sign-up required.',
  },
  {
    number: '2',
    title: 'Click Count',
    description:
      'Click the Count Words button. The tool instantly calculates seven metrics: word count, characters with spaces, characters without spaces, sentence count, paragraph count, reading time, and speaking time.',
  },
  {
    number: '3',
    title: 'Use the data to optimize',
    description:
      'Compare your word count against the benchmarks for your content type. Check reading time to see if your content matches the time your audience is willing to invest. Use speaking time for scripts and presentation planning.',
  },
];

const wordCountBenchmarks = [
  { type: 'Social media post (X/Twitter)', range: '71-100', status: 'Optimal', notes: 'Shorter posts get more engagement. Lead with the most important point.' },
  { type: 'LinkedIn post', range: '150-300', status: 'Optimal', notes: 'Long enough to tell a story, short enough to read in a feed scroll.' },
  { type: 'Email newsletter', range: '200-500', status: 'Optimal', notes: 'Concise emails have higher click-through rates. Link out for depth.' },
  { type: 'Product description', range: '300-500', status: 'Optimal', notes: 'Enough for features, benefits, and keywords. Match competitor depth.' },
  { type: 'Blog post (short)', range: '800-1,200', status: 'Good', notes: 'Works for news, updates, and simple how-to guides targeting low-competition keywords.' },
  { type: 'Blog post (standard)', range: '1,500-2,500', status: 'Optimal', notes: 'The sweet spot for most SEO content. Enough depth to rank, concise enough to read.' },
  { type: 'Pillar content', range: '3,000-5,000+', status: 'Good', notes: 'Comprehensive topic coverage for competitive keywords. Pair with strong internal linking.' },
];

const wordCountByPurpose = [
  { purpose: 'SEO blog post', target: '1,500-2,500', readingTime: '8-13 min', notes: 'Match the average length of the top 5 ranking pages for your target keyword.' },
  { purpose: 'Sales landing page', target: '500-1,500', readingTime: '3-8 min', notes: 'Long enough to overcome objections, short enough to maintain conversion momentum.' },
  { purpose: 'About page', target: '250-500', readingTime: '1-3 min', notes: 'Concise, credibility-focused. Visitors scan rather than read in depth.' },
  { purpose: 'Case study', target: '800-1,500', readingTime: '4-8 min', notes: 'Tell the problem-solution-result story clearly. Avoid padding with irrelevant detail.' },
  { purpose: 'Podcast script', target: '1,300-1,950', readingTime: 'N/A', notes: 'At 130 wpm speaking pace: 10-15 minutes of content. Adjust by segment.' },
  { purpose: 'Presentation script', target: '650-1,300', readingTime: 'N/A', notes: '5-10 minutes at 130 wpm. Leave room for pauses, questions, and slide transitions.' },
];

const contentMistakes = [
  {
    icon: '📉',
    title: 'Writing thin content for competitive keywords',
    description:
      'A 400-word post cannot compete with a comprehensive 2,000-word guide for a competitive keyword. Search engines evaluate topical depth and content completeness. For competitive queries, analyze the top 5 ranking pages and match or exceed their average word count while maintaining quality.',
    stat: 'Match top-ranking page length for competitive keywords',
  },
  {
    icon: '📝',
    title: 'Padding content to hit a word count target',
    description:
      'Adding filler sentences, repeating information, or using roundabout phrasing to inflate word count produces lower-quality content. Search engines measure engagement signals: time on page, bounce rate, and scroll depth. Padded content drives people away faster, which hurts your rankings.',
    stat: 'Every sentence should add value or be deleted',
  },
  {
    icon: '⏱️',
    title: 'Ignoring reading time in content planning',
    description:
      'A blog post with a 25-minute reading time needs to be exceptional to retain readers. An email with a 12-minute reading time will be ignored. Use reading time as a planning metric. Match your content length to the time your audience is realistically willing to invest.',
    stat: 'Blog posts: aim for 7-12 minute reading time',
  },
  {
    icon: '🔀',
    title: 'Optimizing word count instead of search intent',
    description:
      'A 3,000-word guide answering a question that could be answered in 200 words is frustrating for users. Google rewards pages that best satisfy search intent, not the longest pages. Informational queries need depth. Transactional queries need clarity and conversion-focused brevity.',
    stat: 'Word count should match search intent, not arbitrary targets',
  },
  {
    icon: '📊',
    title: 'Using one word count target for all content types',
    description:
      'Product pages, blog posts, landing pages, and FAQs have different optimal lengths. A 2,000-word product page overwhelms buyers. A 300-word blog post on a competitive topic gets ignored. Research the right length benchmark for each content format and use case.',
    stat: 'Different content types require different word count targets',
  },
  {
    icon: '🔄',
    title: 'Never updating content to maintain word count parity',
    description:
      'Competitors publish longer, more comprehensive content over time. A page that ranked with 1,200 words in 2023 may need 2,000 words in 2026 to compete with updated competitor content. Audit your top pages annually and expand content that has fallen behind in length and depth.',
    stat: 'Audit top pages annually and update word count as needed',
  },
];

const tips = [
  {
    number: '01',
    title: 'Match your word count to competitor content',
    description:
      'Before writing, check the average word count of the top 5 pages ranking for your target keyword. Use a tool like Ahrefs or manually count. Your content should match or slightly exceed this average. Being shorter suggests less thorough coverage to search engines.',
    cta: null,
    ctaHref: null,
  },
  {
    number: '02',
    title: 'Use reading time to set content expectations',
    description:
      'Add reading time estimates to your blog posts and articles. Showing "8 min read" at the top helps readers decide whether to bookmark for later or read now. This reduces quick-bounce behavior from readers who started reading without knowing the commitment involved.',
    cta: null,
    ctaHref: null,
  },
  {
    number: '03',
    title: 'Organize long content with accordion sections',
    description:
      'Content over 2,000 words benefits from accordion-organized FAQ sections and collapsible subsections that prevent overwhelming readers. Accordion widgets let you add substantial word count without forcing readers to scroll through every section to find what they need.',
    cta: 'Try Accordion widget',
    ctaHref: '/widgets/accordion',
  },
  {
    number: '04',
    title: 'Use tabs to segment high word count pages',
    description:
      'Tabs let you split content into focused sections without reducing your total indexed word count. A 4,000-word page organized across four tabs is easier to navigate than 4,000 words in a single scrolling column, while keeping all content crawlable on one URL.',
    cta: 'Try Tabs widget',
    ctaHref: '/widgets/tabs',
  },
  {
    number: '05',
    title: 'Add comparison tables for dense feature content',
    description:
      'Comparison tables communicate a high ratio of information per word. A 10-row comparison table covers as much ground as 500-800 words of prose, with better scannability. Use tables to add word-efficient, highly readable sections to any long-form page.',
    cta: 'Try Comparison Tables widget',
    ctaHref: '/widgets/comparison-tables',
  },
  {
    number: '06',
    title: 'Keep content fresh with dynamic feeds',
    description:
      'Content feeds add new words, headings, and keywords to your pages regularly without manual effort. Fresh content tells search engines your page is actively maintained. Higher crawl frequency means new content gets indexed faster, which compounds your SEO gains over time.',
    cta: 'Try Feeds widget',
    ctaHref: '/widgets/feeds',
  },
  {
    number: '07',
    title: 'Cut words that add length but not value',
    description:
      'Edit ruthlessly after your first draft. Remove phrases like "it is important to note," "in order to," "due to the fact that," and "as mentioned above." These phrases inflate word count without adding meaning. Every sentence should either inform, persuade, or engage.',
    cta: null,
    ctaHref: null,
  },
  {
    number: '08',
    title: 'Calculate speaking time for scripts and presentations',
    description:
      'The average speaker delivers 130 words per minute in a clear, measured presentation pace. Use the speaking time estimate from this tool when writing podcast scripts, webinar presentations, or sales call scripts. Adjust for natural pauses, audience questions, and slide transitions.',
    cta: null,
    ctaHref: null,
  },
];

const glossaryMetrics = [
  {
    term: 'Word Count',
    definition: 'The total number of individual words in a piece of content. One of the most commonly used content length metrics for SEO planning, editorial guidelines, and content quality benchmarking.',
    formula: 'Total space-separated tokens',
    whenToUse: 'Planning content depth and comparing against competitor pages',
  },
  {
    term: 'Character Count',
    definition: 'The total number of individual characters including letters, numbers, punctuation, and spaces. Commonly used for social media posts, meta descriptions, title tags, and other format-constrained content.',
    formula: 'Total characters with/without spaces',
    whenToUse: 'Writing meta tags, social posts, and any character-limited content',
  },
  {
    term: 'Reading Time',
    definition: 'An estimate of how long it takes to read a piece of content at an average adult reading speed of 200 words per minute. Used to set reader expectations and plan content investment for different audience segments.',
    formula: 'Word count / 200 wpm',
    whenToUse: 'Blog posts, articles, and any content where time commitment affects engagement',
  },
  {
    term: 'Speaking Time',
    definition: 'An estimate of how long it takes to deliver content aloud at an average speaking pace of 130 words per minute. Used for scripting podcasts, webinars, video content, and presentations.',
    formula: 'Word count / 130 wpm',
    whenToUse: 'Scripts for podcasts, webinars, sales calls, and live presentations',
  },
  {
    term: 'Content Depth',
    definition: 'A qualitative measure of how thoroughly a page covers its topic. High word count does not guarantee depth. Depth requires covering all relevant subtopics, answering user questions, and providing actionable information that readers and search engines value.',
    formula: 'Topical completeness (qualitative)',
    whenToUse: 'Evaluating whether content deserves to rank for competitive keywords',
  },
];

export const WordCounterContent = ({ relatedPosts }: IProps) => {
  return (
    <StyledContentWrapper>

      {/* How It Works */}
      <StyledSection>
        <StyledInner>
          <StyledSectionLabel>How It Works</StyledSectionLabel>
          <StyledSectionHeading>How to use this free word counter</StyledSectionHeading>
          <StyledSectionIntro>
            No account needed, no sign-up required. Completely free. Paste your text
            and get seven content metrics instantly.
          </StyledSectionIntro>
          <StyledStepsGrid>
            {steps.map((step) => (
              <StyledStep key={step.number}>
                <div className="step-number">{step.number}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </StyledStep>
            ))}
          </StyledStepsGrid>
        </StyledInner>
      </StyledSection>

      {/* The Formula */}
      <StyledSection bg="var(--gray-50)">
        <StyledInner maxWidth="720px">
          <StyledSectionLabel>The Formula</StyledSectionLabel>
          <StyledSectionHeading>
            How word count, reading time, and speaking time are calculated
          </StyledSectionHeading>
          <StyledSectionIntro>
            This free word counter uses industry-standard formulas for every metric.
            Here is exactly how each number is calculated.
          </StyledSectionIntro>

          <StyledFormulaBox>
            <p className="label">Core Formulas</p>
            <p className="formula">Reading time = Words / 200 wpm &nbsp;|&nbsp; Speaking time = Words / 130 wpm</p>
            <p className="example">
              Example: <span>1,500 words = 7.5 min reading time = 11.5 min speaking time</span>
            </p>
          </StyledFormulaBox>

          <StyledFormulaBody>
            <p>
              Word count is calculated by splitting the text on whitespace. Each
              continuous run of non-whitespace characters counts as one word. Numbers,
              hyphenated terms, and contractions each count as one word, matching the
              behavior of major word processors.
            </p>
            <p>
              Character counts are provided in two versions: with spaces (the total count
              of every character including spaces) and without spaces (only letters,
              numbers, and punctuation). The with-spaces count is relevant for character-limited
              fields. The without-spaces count is useful for measuring actual content density.
            </p>
            <p>
              Reading time uses a benchmark of 200 words per minute, which is the
              commonly accepted average for silent reading of online content. Speaking
              time uses 130 words per minute, which reflects a clear, measured
              presentation delivery pace rather than conversational speed.
            </p>
            <p>
              Paragraph count is calculated by detecting double line breaks. Sentence
              count detects sentence-ending punctuation. Both are estimates and may
              vary slightly depending on punctuation style and formatting conventions.
            </p>
          </StyledFormulaBody>
        </StyledInner>
      </StyledSection>

      {/* Benchmarks Table */}
      <StyledSection>
        <StyledInner>
          <StyledSectionLabel>Word Count Benchmarks</StyledSectionLabel>
          <StyledSectionHeading>
            Optimal word counts for every content type
          </StyledSectionHeading>
          <StyledSectionIntro>
            Use this table to set the right length target before you start writing.
            Different formats have different audience expectations and SEO requirements.
          </StyledSectionIntro>

          <StyledDataTable>
            <table>
              <thead>
                <tr>
                  <th>Content Type</th>
                  <th>Word Range</th>
                  <th>Status</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {wordCountBenchmarks.map((row) => (
                  <tr key={row.type}>
                    <td className="col-name">{row.type}</td>
                    <td className="col-value">{row.range}</td>
                    <td>
                      <StyledBadge
                        color={row.status === 'Optimal' ? '#166534' : '#92400e'}
                        bg={row.status === 'Optimal' ? '#dcfce7' : '#fef3c7'}
                      >
                        {row.status}
                      </StyledBadge>
                    </td>
                    <td className="col-note">{row.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </StyledDataTable>

          <StyledTableFootnote>
            Benchmarks based on content performance analysis and industry research, 2026.
          </StyledTableFootnote>
        </StyledInner>
      </StyledSection>

      {/* Word Count by Purpose Table */}
      <StyledSection bg="var(--gray-50)">
        <StyledInner>
          <StyledSectionLabel>Word Count by Purpose</StyledSectionLabel>
          <StyledSectionHeading>
            Target word counts and reading times by content goal
          </StyledSectionHeading>
          <StyledSectionIntro>
            Match your word count to the purpose of your content. Different goals
            require different levels of depth and reading time investment.
          </StyledSectionIntro>

          <StyledDataTable>
            <table>
              <thead>
                <tr>
                  <th>Purpose</th>
                  <th>Target Words</th>
                  <th>Time</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {wordCountByPurpose.map((row) => (
                  <tr key={row.purpose}>
                    <td className="col-name">{row.purpose}</td>
                    <td className="col-value">{row.target}</td>
                    <td className="col-value">{row.readingTime}</td>
                    <td className="col-note">{row.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </StyledDataTable>

          <StyledTableFootnote>
            Averages based on content performance research and industry benchmarks, 2026.
          </StyledTableFootnote>
        </StyledInner>
      </StyledSection>

      {/* 6 Mistakes */}
      <StyledSection>
        <StyledInner>
          <StyledSectionLabel>What Kills Your Content Strategy</StyledSectionLabel>
          <StyledSectionHeading>
            Six word count mistakes that damage your content performance
          </StyledSectionHeading>
          <StyledSectionIntro>
            These mistakes make content either too short to compete, too long to engage,
            or optimized for the wrong metric entirely.
          </StyledSectionIntro>

          <StyledFactorsGrid>
            {contentMistakes.map((factor) => (
              <StyledFactorCard key={factor.title}>
                <span className="factor-icon">{factor.icon}</span>
                <h3>{factor.title}</h3>
                <p>{factor.description}</p>
                <span className="factor-stat">{factor.stat}</span>
              </StyledFactorCard>
            ))}
          </StyledFactorsGrid>
        </StyledInner>
      </StyledSection>

      {/* 8 Tips */}
      <StyledSection bg="var(--gray-50)">
        <StyledInner>
          <StyledSectionLabel>Optimize Your Content Length</StyledSectionLabel>
          <StyledSectionHeading>
            8 tips to use word count as a strategic content tool
          </StyledSectionHeading>
          <StyledSectionIntro>
            These strategies help you plan, write, and optimize content at the right length
            for your goals. All CommonNinja widgets are free to start.
          </StyledSectionIntro>

          <StyledTipsGrid>
            {tips.map((tip) => (
              <StyledTipCard key={tip.number}>
                <div className="tip-header">
                  <div className="tip-number">{tip.number}</div>
                  <h3>{tip.title}</h3>
                </div>
                <p>{tip.description}</p>
                {tip.cta && tip.ctaHref && (
                  <a className="tip-cta" href={tip.ctaHref} target="_blank" rel="noopener noreferrer">
                    {tip.cta} →
                  </a>
                )}
              </StyledTipCard>
            ))}
          </StyledTipsGrid>
        </StyledInner>
      </StyledSection>

      {/* Glossary */}
      <StyledSection>
        <StyledInner>
          <StyledSectionLabel>Content Metrics Glossary</StyledSectionLabel>
          <StyledSectionHeading>
            Key content length and engagement terms explained
          </StyledSectionHeading>
          <StyledSectionIntro>
            Word count is one of several content metrics that inform SEO and editorial
            strategy. Here is how the related terms connect and when each matters.
          </StyledSectionIntro>

          <StyledMetricsTable>
            <table>
              <thead>
                <tr>
                  <th>Term</th>
                  <th>Definition</th>
                  <th>Formula</th>
                  <th>When to Use</th>
                </tr>
              </thead>
              <tbody>
                {glossaryMetrics.map((row) => (
                  <tr key={row.term}>
                    <td className="metric-name">{row.term}</td>
                    <td>{row.definition}</td>
                    <td><span className="metric-formula">{row.formula}</span></td>
                    <td>{row.whenToUse}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </StyledMetricsTable>
        </StyledInner>
      </StyledSection>

      {/* Related Articles */}
      {relatedPosts.length > 0 && (
        <StyledSection bg="var(--gray-50)">
          <StyledInner>
            <StyledSectionLabel>From the Blog</StyledSectionLabel>
            <StyledSectionHeading>Further reading on content length, SEO, and writing strategy</StyledSectionHeading>
            <StyledSectionIntro>
              Dig deeper into content optimization, word count strategy, and SEO writing
              best practices that drive organic traffic.
            </StyledSectionIntro>
            <StyledRelatedGrid>
              {relatedPosts.map((post) => (
                <StyledRelatedCard
                  key={post.slug}
                  href={`/blog/${post.slug}`}
                >
                  <h3>{post.name}</h3>
                  {post.excerpt && (
                    <p dangerouslySetInnerHTML={{ __html: clampExcerpt(post.excerpt) }} />
                  )}
                  <span className="read-more">Read article →</span>
                </StyledRelatedCard>
              ))}
            </StyledRelatedGrid>
          </StyledInner>
        </StyledSection>
      )}

    </StyledContentWrapper>
  );
};
